import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Search, FileText, Briefcase, AlertTriangle, Building2, ArrowRight, Command, CornerDownLeft } from 'lucide-react';

const TYPE_CONFIG = {
  entity: { icon: Building2, color: '#1A4C7C', label: 'Entity' },
  policy: { icon: FileText, color: '#1D9E75', label: 'Policy' },
  claim: { icon: Briefcase, color: '#E8A020', label: 'Claim' },
  submission: { icon: AlertTriangle, color: '#00B4D8', label: 'Submission' },
  screen: { icon: ArrowRight, color: '#5C6B7A', label: 'Screen' },
};

const SCREENS = [
  { title: 'Today', path: '/today', subtitle: 'Priority queue' },
  { title: 'New Business', path: '/new-business', subtitle: 'Submissions pipeline' },
  { title: 'Underwriting', path: '/underwriting', subtitle: 'Referral queue' },
  { title: 'Renewals', path: '/renewals', subtitle: 'Renewal pipeline' },
  { title: 'Claims', path: '/claims', subtitle: 'Active claims' },
  { title: 'Policies', path: '/policies', subtitle: 'Policy register' },
  { title: 'Portfolio', path: '/portfolio', subtitle: 'Portfolio overview' },
  { title: 'Portfolio Heatmap', path: '/portfolio?tab=heatmap', subtitle: 'Geographic risk' },
  { title: 'Territory Analytics', path: '/portfolio?tab=territory', subtitle: 'Regional breakdown' },
  { title: 'Broker Scorecard', path: '/portfolio?tab=brokers', subtitle: 'Broker rankings' },
  { title: 'Finance', path: '/finance', subtitle: 'Premium trust & reporting' },
  { title: 'Compliance', path: '/compliance', subtitle: 'Regulatory modules' },
  { title: 'Compliance Calendar', path: '/compliance?tab=calendar', subtitle: 'Deadline timeline' },
  { title: 'Distribution', path: '/distribution', subtitle: 'Broker management' },
  { title: 'Admin', path: '/admin', subtitle: 'Platform configuration' },
  { title: 'Operating Modes', path: '/operating-model?tab=modes', subtitle: '25 deployment modes' },
  { title: 'Flow Map', path: '/operating-model?tab=flow-map', subtitle: 'Every workflow, stage and gate' },
  { title: 'CAT Mode', path: '/claims?tab=cat-mode', subtitle: 'Catastrophe management' },
  { title: 'MARS Agents', path: '/admin?tab=mars', subtitle: 'AI agent pipeline' },
];

export default function CommandPalette({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const api = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  const isBroker = user?.role === 'broker';
  const allowedScreens = isBroker
    ? SCREENS.filter(s => ['/new-business', '/policies', '/claims', '/renewals'].some(p => s.path.startsWith(p)))
    : SCREENS;

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      // Show screens matching query
      const screenResults = allowedScreens.filter(s =>
        s.title.toLowerCase().includes(q.toLowerCase())
      ).map(s => ({ type: 'screen', id: s.path, title: s.title, subtitle: s.subtitle, path: s.path }));
      setResults(screenResults.slice(0, 8));
      return;
    }

    setLoading(true);
    try {
      // Search screens locally
      const screenResults = allowedScreens.filter(s =>
        s.title.toLowerCase().includes(q.toLowerCase())
      ).map(s => ({ type: 'screen', id: s.path, title: s.title, subtitle: s.subtitle, path: s.path }));

      // Search backend
      const { data } = await api.get(`/search/global?q=${encodeURIComponent(q)}&limit=20`);
      const combined = [...screenResults.slice(0, 3), ...(data.results || [])];
      setResults(combined);
    } catch (err) {
      console.error('Search error:', err?.message);
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, isBroker]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 150);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  const selectResult = (result) => {
    if (result?.path) {
      navigate(result.path);
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      selectResult(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Group results by type
  const grouped = {};
  results.forEach(r => {
    const key = r.type || 'other';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  });
  const groupOrder = ['screen', 'entity', 'policy', 'claim', 'submission'];
  let flatIndex = -1;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="command-palette-backdrop" />

      {/* Palette */}
      <div className="fixed z-[101] top-[15%] left-1/2 -translate-x-1/2 w-full max-w-[640px]" data-testid="command-palette">
        <div className="bg-white rounded-lg shadow-2xl overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
            <Search className="w-5 h-5 shrink-0" style={{ color: '#5C6B7A' }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => { setQuery(e.target.value); setSelectedIndex(0); }}
              onKeyDown={handleKeyDown}
              placeholder="Search entities, policies, claims, or navigate..."
              className="flex-1 text-sm bg-transparent outline-none"
              style={{ color: '#0D1B2A' }}
              data-testid="command-palette-input"
            />
            <kbd className="hidden sm:flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#F5F7FA', color: '#5C6B7A', border: '1px solid rgba(136,150,168,0.2)' }}>
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[50vh] overflow-y-auto py-1">
            {results.length === 0 && query.length >= 2 && !loading && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm" style={{ color: '#5C6B7A' }}>No results for "{query}"</p>
              </div>
            )}
            {results.length === 0 && query.length < 2 && (
              <div className="px-4 py-4">
                <p className="text-xs mb-3" style={{ color: '#5C6B7A' }}>Quick navigation</p>
                {SCREENS.slice(0, 6).map(s => {
                  flatIndex++;
                  const idx = flatIndex;
                  return (
                    <button key={s.path} onClick={() => selectResult(s)}
                      className={`flex items-center gap-3 w-full px-3 py-2 rounded-md text-left text-sm transition-colors ${idx === selectedIndex ? 'bg-[#F0F4FF]' : 'hover:bg-[#F5F7FA]'}`}
                      style={{ color: '#0D1B2A' }}>
                      <ArrowRight className="w-4 h-4" style={{ color: '#5C6B7A' }} />
                      <div>
                        <span className="font-medium">{s.title}</span>
                        <span className="ml-2 text-xs" style={{ color: '#5C6B7A' }}>{s.subtitle}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {groupOrder.map(groupType => {
              const items = grouped[groupType];
              if (!items || items.length === 0) return null;
              const cfg = TYPE_CONFIG[groupType] || TYPE_CONFIG.entity;
              return (
                <div key={groupType}>
                  <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#5C6B7A' }}>
                    {cfg.label}s
                  </p>
                  {items.map(result => {
                    flatIndex++;
                    const idx = flatIndex;
                    const Icon = cfg.icon;
                    return (
                      <button key={`${result.type}-${result.id}`} onClick={() => selectResult(result)}
                        className={`flex items-center gap-3 w-full px-4 py-2 text-left text-sm transition-colors ${idx === selectedIndex ? 'bg-[#F0F4FF]' : 'hover:bg-[#F5F7FA]'}`}>
                        <div className="w-7 h-7 rounded flex items-center justify-center shrink-0" style={{ background: cfg.color + '12' }}>
                          <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate" style={{ color: '#0D1B2A' }}>{result.title}</p>
                          <p className="text-xs truncate" style={{ color: '#5C6B7A' }}>{result.subtitle}</p>
                        </div>
                        <CornerDownLeft className="w-3.5 h-3.5 shrink-0 opacity-0 group-hover:opacity-100" style={{ color: '#5C6B7A' }} />
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t text-[10px]" style={{ borderColor: 'rgba(136,150,168,0.15)', color: '#5C6B7A', background: '#FAFBFC' }}>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded" style={{ background: '#F0F2F5', border: '1px solid #E0E6EC' }}>↑↓</kbd> navigate</span>
              <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded" style={{ background: '#F0F2F5', border: '1px solid #E0E6EC' }}>↵</kbd> select</span>
              <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded" style={{ background: '#F0F2F5', border: '1px solid #E0E6EC' }}>esc</kbd> close</span>
            </div>
            {loading && <span className="animate-pulse">Searching...</span>}
          </div>
        </div>
      </div>
    </>
  );
}
